import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { adminAPI, publicAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { Plus, Edit, Trash2 } from 'lucide-react';

const emptyForm = {
  from_currency: '', to_currency: '', buy_rate: 0, sell_rate: 0,
  fee_percentage: 0, min_amount: 0, max_amount: null, enabled: true
};

export const AdminExchangeRatesPage = () => {
  const [pairs, setPairs] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [pairsRes, currsRes] = await Promise.all([
        publicAPI.getExchangeRates(false),
        publicAPI.getCurrencies(true)
      ]);
      setPairs(pairsRes.data);
      setCurrencies(currsRes.data);
    } catch (error) {
      toast.error('Failed to load');
    } finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (pair) => {
    setEditingId(pair.id);
    setForm({
      from_currency: pair.from_currency,
      to_currency: pair.to_currency,
      buy_rate: pair.buy_rate,
      sell_rate: pair.sell_rate,
      fee_percentage: pair.fee_percentage,
      min_amount: pair.min_amount,
      max_amount: pair.max_amount,
      enabled: pair.enabled
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        buy_rate: parseFloat(form.buy_rate),
        sell_rate: parseFloat(form.sell_rate),
        fee_percentage: parseFloat(form.fee_percentage),
        min_amount: parseFloat(form.min_amount),
        max_amount: form.max_amount ? parseFloat(form.max_amount) : null
      };
      if (editingId) {
        await adminAPI.updateExchangePair(editingId, payload);
        toast.success('Pair updated');
      } else {
        await adminAPI.createExchangePair(payload);
        toast.success('Pair created');
      }
      setDialogOpen(false);
      load();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this pair?')) return;
    try {
      await adminAPI.deleteExchangePair(id);
      toast.success('Pair deleted');
      load();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-exchange-rates-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Exchange Pairs</h1>
            <p className="mt-2 text-muted-foreground">Configure currency exchange rates and pairs</p>
          </div>
          <Button onClick={openCreate} data-testid="create-pair-button">
            <Plus className="h-4 w-4 mr-1" /> Add Pair
          </Button>
        </div>

        <Card>
          <CardHeader><CardTitle>All Exchange Pairs</CardTitle></CardHeader>
          <CardContent>
            {loading ? <p className="text-center py-8">Loading...</p> : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pair</TableHead>
                    <TableHead>Buy Rate</TableHead>
                    <TableHead>Sell Rate</TableHead>
                    <TableHead>Fee</TableHead>
                    <TableHead>Min</TableHead>
                    <TableHead>Max</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pairs.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium">{p.from_currency} → {p.to_currency}</TableCell>
                      <TableCell>{p.buy_rate}</TableCell>
                      <TableCell>{p.sell_rate}</TableCell>
                      <TableCell>{p.fee_percentage}%</TableCell>
                      <TableCell>{p.min_amount}</TableCell>
                      <TableCell>{p.max_amount || '-'}</TableCell>
                      <TableCell>
                        <Badge className={p.enabled ? 'bg-green-600' : 'bg-gray-500'}>
                          {p.enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(p)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(p.id)}>
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit Exchange Pair' : 'Create Exchange Pair'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>From Currency</Label>
                  <Select value={form.from_currency} onValueChange={(v) => setForm({...form, from_currency: v})} disabled={!!editingId}>
                    <SelectTrigger data-testid="pair-from-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {currencies.map(c => <SelectItem key={c.symbol} value={c.symbol}>{c.symbol}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>To Currency</Label>
                  <Select value={form.to_currency} onValueChange={(v) => setForm({...form, to_currency: v})} disabled={!!editingId}>
                    <SelectTrigger data-testid="pair-to-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {currencies.filter(c => c.symbol !== form.from_currency).map(c => (
                        <SelectItem key={c.symbol} value={c.symbol}>{c.symbol}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Buy Rate</Label>
                  <Input type="number" step="0.000001" value={form.buy_rate} onChange={(e) => setForm({...form, buy_rate: e.target.value})} required />
                </div>
                <div className="space-y-2">
                  <Label>Sell Rate</Label>
                  <Input type="number" step="0.000001" value={form.sell_rate} onChange={(e) => setForm({...form, sell_rate: e.target.value})} required />
                </div>
                <div className="space-y-2">
                  <Label>Fee %</Label>
                  <Input type="number" step="0.01" value={form.fee_percentage} onChange={(e) => setForm({...form, fee_percentage: e.target.value})} required />
                </div>
                <div className="space-y-2">
                  <Label>Min Amount</Label>
                  <Input type="number" step="0.01" value={form.min_amount} onChange={(e) => setForm({...form, min_amount: e.target.value})} required />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Max Amount (optional)</Label>
                  <Input type="number" step="0.01" value={form.max_amount || ''} onChange={(e) => setForm({...form, max_amount: e.target.value || null})} />
                </div>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <Label>Enabled</Label>
                <Switch checked={form.enabled} onCheckedChange={(v) => setForm({...form, enabled: v})} />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="save-pair-button">{editingId ? 'Update' : 'Create'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};
